import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import Badge, { BadgeBg } from 'components/base/Badge';
import { formatLongIsoDate } from 'utils/formatLongIsoDate';
import { ProductMovementClass } from 'utils/types';

type ProductMovementBadgeProps = {
  movementClass?: ProductMovementClass | null;
  netQuantitySoldLast90Days?: number | null;
  distinctSaleDaysLast90Days?: number | null;
  lastSoldOn?: string | null;
  firstDeliveredOn?: string | null;
  tooltipId?: string;
};

const formatQty = (qty: number) =>
  Number.isInteger(qty) ? String(qty) : qty.toFixed(1);

const formatAge = (dateString: string) => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return formatLongIsoDate(dateString);
  }

  const days = Math.floor((Date.now() - date.getTime()) / 86_400_000);
  if (days < 60) {
    return `${Math.max(days, 0)} days ago`;
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    return `${months} month${months === 1 ? '' : 's'} ago`;
  }

  const years = Math.floor(days / 365);
  const leftoverMonths = Math.floor((days % 365) / 30);
  if (years === 1 && leftoverMonths > 0) {
    return `1 year ${leftoverMonths} month${leftoverMonths === 1 ? '' : 's'} ago`;
  }

  return `${years} year${years === 1 ? '' : 's'} ago`;
};

const badgeCopy = (
  movementClass: Exclude<ProductMovementClass, 'None'>,
  props: ProductMovementBadgeProps
): { label: string; bg: BadgeBg; tooltip: string } | null => {
  if (
    movementClass === 'Fast' ||
    movementClass === 'Medium' ||
    movementClass === 'Slow'
  ) {
    const qty = props.netQuantitySoldLast90Days ?? 0;
    const days = props.distinctSaleDaysLast90Days ?? 0;
    const label =
      movementClass === 'Fast'
        ? 'Fast moving'
        : movementClass === 'Medium'
          ? 'Medium moving'
          : 'Slow';
    const bg: BadgeBg =
      movementClass === 'Fast'
        ? 'success'
        : movementClass === 'Medium'
          ? 'info'
          : 'secondary';
    return {
      label,
      bg,
      tooltip: `Sold ${formatQty(qty)} on ${days} day${
        days === 1 ? '' : 's'
      } in the last 90 days`
    };
  }

  if (movementClass === 'UltraSlow') {
    if (props.lastSoldOn) {
      return {
        label: 'Ultra slow',
        bg: 'warning',
        tooltip: `Last sold ${formatAge(props.lastSoldOn)} (${formatLongIsoDate(
          props.lastSoldOn
        )})`
      };
    }

    if (props.firstDeliveredOn) {
      return {
        label: 'Ultra slow',
        bg: 'warning',
        tooltip: `Never sold. First received ${formatAge(
          props.firstDeliveredOn
        )} (${formatLongIsoDate(props.firstDeliveredOn)})`
      };
    }

    return {
      label: 'Ultra slow',
      bg: 'warning',
      tooltip: 'No sale in the last year'
    };
  }

  return null;
};

const ProductMovementBadge = ({
  movementClass,
  tooltipId,
  ...props
}: ProductMovementBadgeProps) => {
  if (
    movementClass !== 'Fast' &&
    movementClass !== 'Medium' &&
    movementClass !== 'Slow' &&
    movementClass !== 'UltraSlow'
  ) {
    return null;
  }

  const copy = badgeCopy(movementClass, { movementClass, ...props });
  if (!copy) {
    return null;
  }

  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id={tooltipId ?? `movement-${movementClass}`}>
          {copy.tooltip}
        </Tooltip>
      }
    >
      <span className="d-inline-flex">
        <Badge bg={copy.bg} className="fs-10" style={{ cursor: 'help' }}>
          {copy.label}
        </Badge>
      </span>
    </OverlayTrigger>
  );
};

export default ProductMovementBadge;
